import { createContext, useContext, type ReactNode } from "react";
import { useQuery, type QueryObserverResult } from "@tanstack/react-query";
import { fetchMe } from "@/features/home/api/home-api";
import type { MeResponse } from "@/features/home/types";

// =============================================================================
// Types
// =============================================================================

export const AUTH_STATUS = {
  PENDING: "pending",
  AUTHENTICATED: "authenticated",
  UNAUTHENTICATED: "unauthenticated",
} as const;

export type AuthStatus = (typeof AUTH_STATUS)[keyof typeof AUTH_STATUS];

export const ME_QUERY_KEY = ["me"] as const;

interface UserContextValue {
  user: MeResponse | null;
  status: AuthStatus;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<QueryObserverResult<MeResponse>>;
}

// =============================================================================
// Context
// =============================================================================

const UserContext = createContext<UserContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

/**
 * Fetches GET /v1/users/me once on mount and shares the result via context.
 * StrictMode-safe — TanStack Query deduplicates the request.
 * useMe() and AuthGuard both read from here; no duplicate API calls.
 *
 * Status derivation:
 * - isLoading            → PENDING   (first fetch in-flight, no data yet)
 * - data available       → AUTHENTICATED
 * - isError (e.g. 401)  → UNAUTHENTICATED
 * - no data, no error    → UNAUTHENTICATED (edge case: query disabled)
 *
 * Note: a transient server error (5xx) will map to UNAUTHENTICATED and
 * redirect to /login. If that becomes a problem, add retry logic or a
 * separate ERROR status to distinguish network failures from auth failures.
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
  });

  const status: AuthStatus = isLoading
    ? AUTH_STATUS.PENDING
    : data
      ? AUTH_STATUS.AUTHENTICATED
      : AUTH_STATUS.UNAUTHENTICATED;

  return (
    <UserContext.Provider
      value={{
        user: data ?? null,
        status,
        isLoading,
        error: isError ? error.message : null,
        refetch,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Reads from UserContext. Must be used inside UserProvider.
 * @throws If called outside of UserProvider.
 */
export function useUserContext(): UserContextValue {
  const ctx = useContext(UserContext);

  if (!ctx) {
    throw new Error("useUserContext must be used within a <UserProvider>");
  }

  return ctx;
}
